import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { HelpCircle } from 'lucide-react';

interface FaqItem {
  question: string;
  answer: string;
}

const faqItems: FaqItem[] = [
  {
    question: '¿Qué es Prixia?',
    answer:
      'Prixia es una plataforma integral de inteligencia de precios que ayuda a las empresas a crear cotizaciones profesionales, gestionar modelos de pricing dinámicos, automatizar workflows de aprobación y obtener insights estratégicos con inteligencia artificial. Está diseñada específicamente para equipos comerciales que quieren dejar de depender de hojas de cálculo.',
  },
  {
    question: '¿Cómo accedo a Prixia?',
    answer:
      'Solicitá una demo y armamos juntos el plan que se ajusta a tu equipo y volumen. Te mostramos Prixia funcionando con datos de tu industria en una sesión de 30 minutos por Zoom — sin compromiso ni tarjeta de crédito.',
  },
  {
    question: '¿Cómo funciona el asistente de IA?',
    answer:
      'Nuestro asistente de IA está integrado directamente en la plataforma y entiende tu contexto de negocio. Está construido sobre Claude (Anthropic), así que combina razonamiento avanzado con tus datos reales: responde consultas sobre cotizaciones, analiza márgenes y rentabilidad, sugiere estrategias de pricing basadas en datos históricos y te ayuda a identificar oportunidades de mejora. Funciona como un analista de pricing disponible 24/7.',
  },
  {
    question: '¿Mis datos están seguros?',
    answer:
      'La seguridad es nuestra prioridad. Utilizamos encriptación de extremo a extremo, autenticación JWT con tokens de refresco, y una arquitectura multi-tenant que asegura que los datos de cada empresa están completamente aislados. Cumplimos con las mejores prácticas de seguridad de la industria y realizamos auditorías periódicas.',
  },
  {
    question: '¿Qué integraciones ofrece?',
    answer:
      'Prixia cuenta con una API REST completa que permite integrarse con cualquier sistema. Además, ofrecemos integraciones nativas con SAP Business One, SAP C4C, Odoo, Salesforce, HubSpot, Shopify, VTEX y Magento. Nuestro equipo de soporte puede ayudarte a configurar integraciones personalizadas.',
  },
  {
    question: '¿Puedo exportar cotizaciones en PDF?',
    answer:
      'Sí, todas las cotizaciones se pueden exportar como PDFs profesionales con el branding de tu empresa. Los PDFs incluyen todas las secciones, cálculos detallados, términos y condiciones, e información de contacto. Podés personalizar el diseño y enviarlos directamente a tus clientes desde la plataforma.',
  },
  {
    question: '¿Soporta múltiples monedas?',
    answer:
      'Sí, Prixia soporta múltiples monedas con tipos de cambio actualizados automáticamente. Configurás la moneda base de tu empresa y agregás las que necesites. Las cotizaciones pueden generarse en cualquier moneda configurada, facilitando el trabajo con clientes internacionales.',
  },
  {
    question: '¿Cuántos usuarios puedo tener?',
    answer:
      'Depende del plan que armemos. El Starter cubre equipos de hasta 5 usuarios, el Pro hasta 15, y el Enterprise se adapta al tamaño de tu organización. Cada usuario puede tener roles diferentes (administrador, vendedor, aprobador) con permisos específicos según su función.',
  },
];

export function FaqSection() {
  return (
    <section id="faq" className="py-16 md:py-24 bg-gray-50 dark:bg-gray-900/50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal direction="up">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 text-blue-700 dark:text-blue-400 text-sm font-medium mb-4">
              <HelpCircle className="w-4 h-4" />
              <span>FAQ</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
              Preguntas Frecuentes
            </h2>
            <p className="text-base md:text-lg text-gray-500 dark:text-gray-400">
              Respuestas a las preguntas más comunes sobre Prixia.
            </p>
          </div>
        </ScrollReveal>

        <div className="space-y-3">
          {faqItems.map((faq, index) => (
            <ScrollReveal key={index} delay={index * 0.05} direction="up">
              <details className="group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 dark:backdrop-blur-sm shadow-sm transition-shadow hover:shadow-md dark:hover:shadow-gray-900/30">
                <summary className="flex cursor-pointer items-center justify-between gap-4 p-5 text-left text-sm md:text-base font-semibold text-gray-900 dark:text-white [&::-webkit-details-marker]:hidden list-none">
                  <span>{faq.question}</span>
                  <span className="shrink-0 w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-transform group-open:rotate-45">
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 4.5v15m7.5-7.5h-15"
                      />
                    </svg>
                  </span>
                </summary>
                <div className="px-5 pb-5 pt-0">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </details>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
